import { Fragment, Suspense, useContext } from "react"
import { Route, Routes, Navigate } from "react-router-dom"
import { useCacheHydrate } from "source/shared/react-cache-but-its-a-context"
import { WebBootContext } from "source/publishing/WebBoot"
import { useSession } from "source/shared/hooks/useSession"
import { MyProfileLinks } from "source/myChurchCenter/Profile/MyProfileLinks"
import {
  BreadcrumbNavigationWithMe,
  BreadcrumbPage,
  Loading,
} from "source/shared/components"
import {
  useApiRead,
  SessionApiResource,
} from "source/shared/SessionApiResource"
import { useOrganizationFlag } from "source/payments/hooks/useOrganization"
import { DraftModeProfilePane } from "source/publishing/DraftMode"
import { useDirectoryStatus } from "source/directory/hooks/useDirectoryStatus"
import { ProfileAvatar } from "./ProfileAvatar"
import { ContactAndInfo } from "./ContactAndInfo"
import { ProfileAndHousehold } from "./ProfileAndHousehold"
import { PaymentMethodsPage } from "../../payments/components/PaymentMethodsPage"
import { Heading } from "@planningcenter/doxy-web"
const ME_PATH =
  "/people/v2/me?include=emails,phone_numbers,addresses,marital_status,school"
import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"

export function Profile() {
  const enableSavedPaymentMethods = useOrganizationFlag(
    "useSavedPaymentMethods",
  )

  const { stripe } = useContext(ServerRenderedProps)

  const customFieldsEnabled = useFlipperFeatureEnabled("ROLLOUT_custom_fields")

  // TODO: <Suspense> can be removed once we get rid of the enableSavedPaymentMethods flipper check.
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route
          path="/profile-and-household"
          element={<ProfileAndHousehold />}
        />
        <Route
          path="/contact-and-info/*"
          element={
            customFieldsEnabled ? (
              <Navigate to="/me/profile-and-household" replace />
            ) : (
              <ContactAndInfo />
            )
          }
        />
        {enableSavedPaymentMethods && stripe.account_identifier && (
          <Route path="/payment-methods/*" element={<PaymentMethodsPage />} />
        )}
        <Route path="*" element={<ProfileMenu />} />
      </Routes>
    </Suspense>
  )
}

function ProfileMenu() {
  // TODO useNarrowLayout()
  useSession(true)
  const profile = useApiRead(ME_PATH)
  const { stripe } = useContext(ServerRenderedProps)

  const reloadProfile = useCacheHydrate(SessionApiResource, ME_PATH)
  const name = profile.data.attributes.name
  const avatar =
    profile.data.attributes.avatar_url ||
    profile.data.attributes.demographic_avatar_url

  const showPaymentMethodsLink =
    useOrganizationFlag("useSavedPaymentMethods") && stripe.account_identifier

  return (
    <Fragment>
      <BreadcrumbNavigationWithMe>
        <BreadcrumbPage pageText="My profile and settings" isActive />
      </BreadcrumbNavigationWithMe>
      <div className="d-f ai-c mb-2">
        <ProfileAvatar
          avatar={avatar}
          hasCustomAvatar={!!profile.data.attributes.avatar_url}
          name={name}
          personPath={ME_PATH}
          reloadProfile={reloadProfile}
        />
        <Heading level={1} size={2} text={name} />
      </div>
      <MyProfileLinks
        {...useProfileBuiltFromWebBootContext(showPaymentMethodsLink)}
      />
      <DraftModeProfilePane />
    </Fragment>
  )
}

function useProfileBuiltFromWebBootContext(showPaymentMethodsLink) {
  const DIRECTORY_STATUS_NO_ACCESS = "no_access"

  const person = useSession(false)
  const {
    abilities: {
      people: {
        add_or_remove_household_members: addOrRemoveHouseholdMembersEnabled,
      },
    },
    builtInPages,
    myChurchCenterEnabled,
  } = useContext(WebBootContext)

  const customFieldsEnabled = useFlipperFeatureEnabled("ROLLOUT_custom_fields")

  const directoryStatus = useDirectoryStatus()

  const {
    data: [household],
    meta: { total_count: numberOfHouseholds },
  } = useApiRead(
    `/people/v2/people/${person.data.id}/households?fields[Household]=id`,
  )

  if (!person.meta.authenticated) return { links: [] }

  const householdRoute =
    numberOfHouseholds === 0 && addOrRemoveHouseholdMembersEnabled
      ? "/households/new"
      : numberOfHouseholds === 1
        ? `/households/${household.id}`
        : "/households"

  const profileAndHouseholdsRoute =
    numberOfHouseholds === 1
      ? `/households/${household.id}`
      : numberOfHouseholds > 1
        ? "/households"
        : "/me/profile-and-household"

  const profilePages = customFieldsEnabled
    ? {
        profileAndHouseholds: {
          to: profileAndHouseholdsRoute,
          children: "My profile and household",
          icon: "churchCenter#profile-info-o",
        },
        services: {
          to: "/me",
          children: "Schedule",
          icon: "churchCenter#services-o",
        },
        directory: {
          to: "/directory/edit",
          children: "Directory profile",
          icon: "directory",
        },
        paymentMethods: {
          to: "/me/profile/payment-methods",
          children: "Payment methods",
          icon: "churchCenter#payment-methods-o",
        },
      }
    : {
        contactAndInfo: {
          to: "/me/profile/contact-and-info",
          children: "Contact & profile information",
          icon: "churchCenter#profile-info-o",
        },
        services: {
          to: "/me",
          children: "Schedule",
          icon: "churchCenter#services-o",
        },
        households: {
          to: householdRoute,
          children: "Household",
          icon: "household",
        },
        directory: {
          to: "/directory/edit",
          children: "Directory profile",
          icon: "directory",
        },
        paymentMethods: {
          to: "/me/profile/payment-methods",
          children: "Payment methods",
          icon: "churchCenter#payment-methods-o",
        },
      }

  const nativeProfilePages = !myChurchCenterEnabled ? ["services"] : []

  const defaultPages = customFieldsEnabled
    ? ["profileAndHouseholds"]
    : ["contactAndInfo", "households"]

  showPaymentMethodsLink && defaultPages.push("paymentMethods")

  const hasAccess = (page) => {
    if (page !== "directory") return true

    return directoryStatus !== DIRECTORY_STATUS_NO_ACCESS
  }

  const shouldLinkToPage = (page) =>
    (builtInPages[page] === "published" && hasAccess(page)) ||
    nativeProfilePages.includes(page) ||
    defaultPages.includes(page)

  const links = Object.keys(profilePages).reduce((acc, page) => {
    return shouldLinkToPage(page) ? [...acc, profilePages[page]] : acc
  }, [])

  return { links }
}
